import React, { useEffect, useState, useCallback } from "react";
import { Activity, CheckCheck, RefreshCw, MonitorSmartphone, Globe, ShieldAlert } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso || "—"; } };
const device = (ua = "") => {
  const s = ua.toLowerCase();
  if (/iphone|android.*mobile|windows phone/.test(s)) return "Mobile";
  if (/ipad|tablet/.test(s)) return "Tablet";
  if (/mac os x/.test(s)) return "Mac";
  if (/windows nt/.test(s)) return "Windows";
  if (/linux/.test(s)) return "Linux";
  return "Browser";
};

// Admin-only feed of borrower sign-ins. Polls the tracker, shows an unseen
// badge, and lets the desk mark the feed as reviewed.
export const AccessActivity = ({ client }) => {
  const [events, setEvents] = useState([]);
  const [unseen, setUnseen] = useState(0);
  const [seenAt, setSeenAt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/access-activity?limit=100");
      setEvents(r.data.events || []);
      setUnseen(r.data.unseen || 0);
      setSeenAt(r.data.seen_at || "");
    } catch { /* noop */ }
  }, [client]);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const markSeen = async () => {
    setBusy(true);
    try { await client.post("/admin/access-activity/seen", {}); await load(); } catch { /* noop */ }
    setBusy(false);
  };

  return (
    <div className="bg-[#13130E] border border-[#2A2A22] rounded-lg p-6" data-testid="access-activity-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Activity size={17} className="text-[#E4C97A]" />
          <span className="font-serif text-lg text-[#ECECE4]">Borrower Access Activity</span>
          {unseen > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E4C97A] text-black" data-testid="access-unseen-badge">
              {unseen} new
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} title="Refresh" className="p-1.5 rounded hover:bg-white/10 text-[#9A9A90]" data-testid="access-refresh-btn"><RefreshCw size={14} /></button>
          <button onClick={markSeen} disabled={busy || unseen === 0} data-testid="access-mark-seen-btn"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] border border-[#0B6B4F] text-[#5fd3a8] hover:bg-[#0B6B4F]/20 px-3 py-1.5 rounded-md disabled:opacity-40">
            <CheckCheck size={13} /> Mark all seen
          </button>
        </div>
      </div>
      <p className="text-[11px] text-[#8A8A8A] mb-4">Every borrower sign-in is logged here — who accessed their account, when, and from where.</p>

      {events.length === 0 ? (
        <div className="text-[12px] text-[#6f6f66] py-6 text-center">No borrower sign-ins recorded yet.</div>
      ) : (
        <div className="divide-y divide-[#22221c] max-h-[420px] overflow-y-auto" data-testid="access-activity-list">
          {events.map((e) => {
            const isNew = (e.at || "") > (seenAt || "");
            return (
              <div key={e.id} className="flex items-center gap-3 py-2.5" data-testid={`access-event-${e.id}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${isNew ? "bg-[#E4C97A]" : "bg-transparent border border-[#3a3a30]"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[#ECECE4] truncate">{e.name || e.email} <span className="text-[#7f7f76] text-[11px]">· {e.email}</span></div>
                  <div className="text-[10.5px] text-[#8A8A8A] flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="inline-flex items-center gap-1"><MonitorSmartphone size={11} /> {device(e.user_agent)}</span>
                    <span className="inline-flex items-center gap-1"><Globe size={11} /> {e.ip || "—"}</span>
                    {e.new_device && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#9E2A2B]/20 text-[#FF9E9E] border border-[#9E2A2B]/50" data-testid={`access-new-device-${e.id}`}>
                        <ShieldAlert size={10} /> New device
                      </span>
                    )}
                    {e.new_location && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded bg-[#B8860B]/20 text-[#E4C97A] border border-[#B8860B]/50" data-testid={`access-new-location-${e.id}`}>
                        <ShieldAlert size={10} /> New location
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-[11px] text-[#9A9A90] shrink-0">{fmt(e.at)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Compact per-borrower sign-in history for the Account details panel.
export const BorrowerAccessHistory = ({ apiBase, token, borrowerId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!borrowerId) return;
    let ok = true;
    fetch(`${apiBase}/admin/access-activity/borrower/${borrowerId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => { if (ok) setData(d); }).catch(() => {});
    return () => { ok = false; };
  }, [apiBase, token, borrowerId]);
  if (!borrowerId) return null;
  return (
    <div className="rounded-lg border border-[#2A2A22] p-4 bg-[#0F0F0A]" data-testid="borrower-access-history">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#E4C97A] font-bold">Sign-in History</span>
        <span className="text-[11px] text-[#8A8A8A]">{data ? `${data.login_count || 0} total · last ${data.last_login_at ? fmt(data.last_login_at) : "never"}` : "…"}</span>
      </div>
      {data && data.events && data.events.length > 0 ? (
        <div className="divide-y divide-[#22221c] max-h-[180px] overflow-y-auto">
          {data.events.map((e) => (
            <div key={e.id} className="flex items-center justify-between py-1.5 text-[11px]" data-testid={`bah-event-${e.id}`}>
              <span className="text-[#9A9A90] inline-flex items-center gap-1.5">
                <MonitorSmartphone size={11} /> {device(e.user_agent)} · {e.ip || "—"}
                {(e.new_device || e.new_location) && <ShieldAlert size={11} className="text-[#FF9E9E]" title="New device / location" />}
              </span>
              <span className="text-[#8A8A8A]">{fmt(e.at)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-[#6f6f66] py-2">No sign-ins recorded for this borrower yet.</div>
      )}
    </div>
  );
};

export default AccessActivity;
